import React from 'react';

// One dashboard alert component (Dashboard alert component brief). Replaces the
// two hand-built, mismatched supplier-dashboard banners and is shared so admin
// alerts can use it too rather than growing a third implementation.
//
// Fixed spec (brief §1):
//   grid        40px tile · 14px gap · 1fr text · auto action
//   alignment   items-start ALWAYS — a 2-line and a 4-line alert stacked share
//               a top edge (centring is what read as broken)
//   tile        40×40, radius 10, tone tile colour
//   icon        20px, 2px stroke
//   padding     16px 18px · radius 12 · 1px border
//   heading     15.5px / 600 / 1.4
//   body        13.5px / 1.5
//   action      align-self: center, one only
//
// The ICON differs by MEANING, not just colour (§1/§3) — colour alone fails for
// colour-blind users + high-contrast modes, so each tone owns a distinct glyph
// and the heading always carries the meaning in words.
//
// a11y (§3): role="alert" only for genuinely urgent tones (it interrupts screen
// readers); informational tones use role="status". Icons are aria-hidden.

const TONES = {
  red:   { bg: '#FCEAE6', border: '#E0290A33', tile: '#E0290A1F', icon: '#C11F05', urgent: true },
  amber: { bg: '#FBF3E4', border: '#E89A2E40', tile: '#E89A2E24', icon: '#7A4E10', urgent: false },
  blue:  { bg: '#EAF3F5', border: '#2E8FA140', tile: '#2E8FA11F', icon: '#2C6B82', urgent: false },
  green: { bg: '#E9F3DC', border: '#00915933', tile: '#0091591F', icon: '#0B6B36', urgent: false },
};

// Glyph per tone (20px, 2px stroke). clock=deadline, warning=needs-action,
// refresh=changed, tick=positive.
const GLYPH = {
  red:   <><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></>,
  amber: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  blue:  <><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>,
  green: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
};

export default function Alert({ tone = 'blue', title, body, action = null, glyph = null, background = null, className = '' }) {
  const t = TONES[tone] || TONES.blue;
  return (
    <div
      role={t.urgent ? 'alert' : 'status'}
      className={`relative overflow-hidden grid items-start gap-3.5 rounded-xl border font-['Metropolis'] ${className}`}
      style={{
        gridTemplateColumns: action ? '40px 1fr auto' : '40px 1fr',
        background: t.bg,
        borderColor: t.border,
        padding: '16px 18px',
      }}
    >
      {/* Optional decorative background layer (e.g. streamers) — behind the
          content, aria-hidden, pointer-events:none. The alert must read fine
          without it; content sits above via position:relative below. */}
      {background}
      <span
        aria-hidden="true"
        className="flex items-center justify-center"
        style={{ width: 40, height: 40, borderRadius: 10, background: t.tile, position: 'relative', zIndex: 1 }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={t.icon} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {glyph || GLYPH[tone] || GLYPH.blue}
        </svg>
      </span>
      <div className="min-w-0" style={{ position: 'relative', zIndex: 1 }}>
        <p className="text-ink" style={{ fontSize: '15.5px', fontWeight: 600, lineHeight: 1.4 }}>{title}</p>
        {body && <p className="text-[#4C4E42] mt-1" style={{ fontSize: '13.5px', lineHeight: 1.5 }}>{body}</p>}
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          style={{ position: 'relative', zIndex: 1 }}
          className="self-center whitespace-nowrap rounded-lg border border-[#D4D9C6] bg-white px-4 py-2 text-[13px] font-semibold text-ink hover:border-[#B9C0A9] transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
